import '../styles/globals.css'
import React, { ReactNode, useContext, useEffect, useState } from 'react'
import App, { AppContext, AppProps } from 'next/app'

import {
  AwazeThemeProvider,
  Brand,
} from '@awaze/design-system.components.awaze-theme-provider'
import { GlobalStyle } from '@awaze/design-system.components.global-style'

import { ScreenWrapper } from '../src/components/screenWrapper/screenWrapper'
import { AppContextProvider } from '../src/contexts'
import { GlobalStyleExtended } from '../src/components/global-style-extended/global-style-extended'
import { Store } from '../src/store/type'

import { fieldsParser } from 'contentful-parsers'
import { getBrandConfig } from '../utils/contentful/brandConfig'
import { getContentfulClient } from '../utils/contentful/client'
import { TypeFooterSkeleton } from '../@types/generated/contentful-global'
import { Entry, EntryCollection } from 'contentful'
import {
  MobileMenuType,
  DesktopMenuType,
} from '@awaze/design-system.compositions.responsive-header/navigation-item/navigation-item.types'
import { getInitialPageDataByPath, TMSDefaultPageProps } from '../src/lib/tms'
import { TmsContext, TmsProvider } from '@awaze/mfe-common.contexts.tms'
import { getOptimizelyUserId } from '@awaze/mfe-common.lib.optimizely'
import { TmsEventType } from '@awaze/mfe-common.contexts.tms/dist/DataLayer/DataLayer.types'
import { OptimizelyAttributes } from '../src/lib/optimizely/types'
import {
  createInstance,
  OptimizelyProvider,
  errorHandler,
  setLogger,
} from '@optimizely/react-sdk'
import { isGuestApp as getIsGuestApp } from '../utils/headers'
import { getWishlistIconDecision } from '../src/lib/optimizely/utils'

setLogger(null)

type TProps = Pick<AppProps, 'Component' | 'pageProps'> & {
  brand: Brand
  env: Store['environment']
  headerData: HeaderData | null
  footerData: FooterData | null
  isPageNotFound: boolean
  locale: string
  currentPath: string
  showPhoneIcon: boolean
  shouldShowWishlistIcon: boolean
  optimizelyKey: string
  isExperimentalMode: boolean
  optimizelyUserId: string
  isGuestApp?: boolean
}

const MyAppTmsWrapper = ({
  Component,
  pageProps,
  brand,
  env,
  headerData,
  footerData,
  isPageNotFound,
  locale,
  currentPath,
  showPhoneIcon,
  shouldShowWishlistIcon,
  isExperimentalMode,
  optimizelyUserId,
  optimizelyKey,
  isGuestApp,
}: TProps): ReactNode => {
  return (
    <TmsProvider
      pageLoadDataObject={{
        ...TMSDefaultPageProps({
          brand,
          environment: env,
          ...getInitialPageDataByPath(currentPath),
          locale: locale ?? 'en-GB',
        }),
      }}
    >
      <MyApp
        isExperimentalMode={isExperimentalMode}
        optimizelyKey={optimizelyKey}
        Component={Component}
        pageProps={pageProps}
        brand={brand}
        env={env}
        headerData={headerData}
        footerData={footerData}
        isPageNotFound={isPageNotFound}
        locale={locale}
        currentPath={currentPath}
        optimizelyUserId={optimizelyUserId}
        showPhoneIcon={showPhoneIcon}
        shouldShowWishlistIcon={shouldShowWishlistIcon}
        isGuestApp={isGuestApp}
      />
    </TmsProvider>
  )
}

const MyApp = ({
  Component,
  pageProps,
  brand,
  env,
  headerData,
  footerData,
  isPageNotFound,
  locale,
  currentPath,
  showPhoneIcon,
  shouldShowWishlistIcon,
  optimizelyKey,
  isExperimentalMode,
  optimizelyUserId,
  isGuestApp,
}: TProps): ReactNode => {
  const optimizelyClient = createInstance({
    sdkKey: optimizelyKey,
    errorHandler,
  })
  const tms = useContext(TmsContext)
  const [pageIsViewed, setPageIsViewed] = useState('')

  // Prevent multiple updates.
  // Be aware locally that Node development will trigger this twice.
  useEffect(() => {
    if (window?.tmsevents && pageIsViewed !== currentPath) {
      tms?.updateDataLayer(TmsEventType.view, 'vpv-authentication', {
        ...tms?.pageLoadDataObject,
        is_spa: undefined,
      })
      setPageIsViewed(currentPath)
    }
  }, [tms, pageIsViewed, currentPath])

  return (
    <AwazeThemeProvider brand={brand}>
      <OptimizelyProvider
        optimizely={optimizelyClient}
        user={{
          id: optimizelyUserId,
          attributes: {
            experimental: isExperimentalMode,
            brand,
            locale,
            isGuestApp,
          } as OptimizelyAttributes,
        }}
      >
        <AppContextProvider
          environment={env}
          lang={locale}
          brand={brand}
          currentPath={currentPath}
        >
          <GlobalStyle />
          <GlobalStyleExtended />
          <ScreenWrapper
            headerData={headerData}
            footerData={footerData}
            isPageNotFound={isPageNotFound}
            showPhoneIcon={showPhoneIcon}
            shouldShowWishlistIcon={shouldShowWishlistIcon}
          >
            <Component {...pageProps} />
          </ScreenWrapper>
        </AppContextProvider>
      </OptimizelyProvider>
    </AwazeThemeProvider>
  )
}

export type HeaderData = {
  mobileMenu: { menuItems: MobileMenuType }
  desktopMenu: { menuItems: DesktopMenuType }
  menuButtonText: string
  closeButtonText: string
}

export type FooterData = Entry<TypeFooterSkeleton>['fields']

const getCookies = (req: unknown): Record<string, string> => {
  if (!!req && typeof req === 'object' && 'cookies' in req && req.cookies) {
    return req.cookies as Record<string, string>
  }
  return {}
}

MyAppTmsWrapper.getInitialProps = async (context: AppContext) => {
  const ctx = await App.getInitialProps(context)
  let brand = context.ctx.req?.headers['x-awaze-brand'] || 'cottages.com'
  brand = Array.isArray(brand) ? brand[0] : brand
  const brandConfig = getBrandConfig(brand as Brand)

  const { locale } = context?.router ?? {}

  const isExperimentalMode =
    context.ctx.req?.headers['x-awaze-experimental'] === 'enabled'

  const {
    MOCK_CONTENTFUL,
    CONTENTFUL_SPACE_ID_GLOBAL,
    OPTIMIZELY_SDK_KEY,
    CONTENTFUL_DELIVERY_ACCESS_TOKEN_GLOBAL,
  } = process.env

  let footerData: EntryCollection<
    TypeFooterSkeleton,
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  > | null = null
  let parsedHeaderData: HeaderData | null = null

  if (MOCK_CONTENTFUL === 'false') {
    const client = getContentfulClient({
      space: CONTENTFUL_SPACE_ID_GLOBAL || '',
      accessToken: CONTENTFUL_DELIVERY_ACCESS_TOKEN_GLOBAL || '',
    })

    const headerEntries = await client.getEntries({
      include: 10,
      content_type: 'landingPageGlobals',
      'sys.id': brandConfig.contenfulEntryIds.header,
      locale: locale === 'default' ? 'en-GB' : locale,
    })

    footerData = await client.getEntries<TypeFooterSkeleton>({
      include: 10,
      'sys.id': brandConfig.contenfulEntryIds.footer,
      locale: locale === 'default' ? 'en-GB' : locale,
    })
    footerData = fieldsParser(footerData.items[0])
    parsedHeaderData = fieldsParser(headerEntries.items.at(0)?.fields?.header)
  }
  if (!parsedHeaderData || !footerData) {
    console.info('Header or footer Contentful data is missing')
  }
  const cookies = getCookies(context.ctx.req)
  const brandsWithIcon = ['novasol', 'fincallorca']
  const showPhoneIcon = brandsWithIcon.includes(brand) && locale === 'de-DE'

  const optimizelyUserId = getOptimizelyUserId({
    cookies,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: context.ctx.res as any,
  })

  const isGuestAppValue = getIsGuestApp(context.ctx.req)

  // Get Optimizely decision for wishlist icon server-side (Optimizely experiment for wishlist icon)
  const shouldShowWishlistIcon = OPTIMIZELY_SDK_KEY
    ? await getWishlistIconDecision(
        OPTIMIZELY_SDK_KEY,
        getCookies(context.ctx.req),
        {
          experimental: isExperimentalMode,
          brand,
          locale: locale ?? 'en-GB',
        } as OptimizelyAttributes,
        parsedHeaderData,
      )
    : false

  return {
    ...ctx,
    brand,
    env: process.env.ENVIRONMENT,
    headerData: parsedHeaderData,
    footerData,
    isPageNotFound: context?.router.pathname === '/404',
    locale,
    isExperimentalMode,
    optimizelyKey: OPTIMIZELY_SDK_KEY,
    currentPath: context?.router.asPath,
    showPhoneIcon,
    shouldShowWishlistIcon,
    optimizelyUserId,
    isGuestApp: isGuestAppValue,
  }
}

export default MyAppTmsWrapper
